import { useState, useEffect, useRef, useMemo } from "react";
import { Image, Type, Hash, List, Quote, FileText, Table } from "lucide-react";

interface Command {
  id: string;
  label: string;
  icon: () => React.ReactNode;
  description: string;
}

interface CommandMenuProps {
  isOpen: boolean;
  onSelect: (command: string) => void;
  onClose: () => void;
  position: { x: number; y: number };
  customCommands?: Array<{
    type: string;
    label: string;
    icon: React.ReactNode;
    description: string;
  }>;
}

const COMMANDS: Command[] = [
  {
    id: 'text',
    label: 'Text',
    icon: () => <Type size={16} />,
    description: 'Plain text paragraph'
  },
  {
    id: 'image',
    label: 'Image',
    icon: () => <Image size={16} />,
    description: 'Upload or embed an image'
  },
  {
    id: 'table',
    label: 'Table',
    icon: () => <Table size={16} />,
    description: 'Insert a data table'
  },
 /*  {
    id: 'document',
    label: 'Blueprint',
    icon: () => <FileText size={16} />,
    description: 'Create a document from blueprint'
  } */
];

export const CommandMenu = ({ isOpen, onSelect, onClose, position, customCommands = [] }: CommandMenuProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const menuRef = useRef<HTMLDivElement>(null);

  // Combine default commands with custom commands
  const allCommands: Command[] = useMemo(() => [
    ...COMMANDS,
    ...customCommands.map(cmd => ({
      id: cmd.type,
      label: cmd.label,
      icon: () => cmd.icon,
      description: cmd.description
    }))
  ], [customCommands]);

  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Adjust position if menu would go off-screen
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Check if menu would go below the viewport
      const wouldOverflow = position.y + menuRect.height > viewportHeight;
      
      if (wouldOverflow) {
        // Position menu above the trigger point instead
        // Subtract menu height plus a small gap (8px)
        setAdjustedPosition({
          x: position.x,
          y: position.y - menuRect.height - 16 // 16px gap (8px above + 8px that was below)
        });
      } else {
        setAdjustedPosition(position);
      }
    }
  }, [isOpen, position]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % allCommands.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + allCommands.length) % allCommands.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onSelect(allCommands[selectedIndex].id);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, selectedIndex, onSelect, onClose, allCommands]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="absolute z-50 w-64 bg-editor-command-bg border border-editor-command-border rounded-lg shadow-lg py-2"
      style={{
        top: adjustedPosition.y,
        left: adjustedPosition.x,
        boxShadow: '0 4px 12px hsl(var(--editor-command-shadow))'
      }}
    >
      {allCommands.map((command, index) => (
        <button
          key={command.id}
          className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-editor-hover transition-colors duration-150 ${
            index === selectedIndex ? 'bg-editor-focus' : ''
          }`}
          onClick={() => onSelect(command.id)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="text-editor-handle">
            {command.icon()}
          </div>
          <div className="flex-1">
            <div className="font-medium text-editor-text text-sm">
              {command.label}
            </div>
            <div className="text-xs text-editor-text-placeholder">
              {command.description}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}; 